import { MotionConfig } from "motion/react";
import { PostHog } from "posthog-js";
import { PostHogProvider } from "posthog-js/react";
import { type ReactNode, useEffect, useRef, useState } from "react";
import { env } from "@/env";
import {
	ANALYTICS_CONSENT_AT_KEY,
	ANALYTICS_CONSENT_CHANGED_EVENT,
	AnalyticsReadyContext,
	clearStoredAnalyticsConsent,
	readAnalyticsConsent,
	useAnalyticsConsentState,
} from "@/lib/analytics";
import { filterAnalyticsEvent } from "@/lib/analytics-policy";
import { clearAnalyticsStorage } from "@/lib/analytics-storage";
import { useI18n } from "@/lib/i18n/use-i18n";
import ConsentBanner from "./ConsentBanner";
import Header from "./Header";
import SiteFooter from "./SiteFooter";

const CONTENT_ID = "content";

export function RootShell({ children }: { children: ReactNode }) {
	const { t } = useI18n();

	return (
		<MotionConfig reducedMotion="user">
			<a
				href={`#${CONTENT_ID}`}
				className="sr-only focus:not-sr-only focus:fixed focus:top-3 focus:left-3 focus:z-[60] focus:rounded-md focus:bg-primary focus:px-3 focus:py-2 focus:text-sm focus:text-primary-foreground"
			>
				{t((t) => t.nav.skip)}
			</a>
			<div className="flex min-h-screen flex-col">
				<Header />
				<main id={CONTENT_ID} className="flex-1">
					{children}
				</main>
				<SiteFooter />
			</div>
			{env.VITE_ANALYTICS_ENABLED && env.VITE_POSTHOG_KEY ? (
				<ConsentBanner />
			) : null}
		</MotionConfig>
	);
}

export function AnalyticsProvider({ children }: { children: ReactNode }) {
	const enabled = env.VITE_ANALYTICS_ENABLED && Boolean(env.VITE_POSTHOG_KEY);
	useEffect(() => {
		if (!enabled) {
			clearAnalyticsStorage(env.VITE_POSTHOG_KEY);
			clearStoredAnalyticsConsent();
		}
	}, [enabled]);
	if (!enabled || !env.VITE_POSTHOG_KEY) {
		return <>{children}</>;
	}

	return (
		<ConsentedAnalyticsProvider apiKey={env.VITE_POSTHOG_KEY}>
			{children}
		</ConsentedAnalyticsProvider>
	);
}

export function ConsentedAnalyticsProvider({
	apiKey,
	children,
}: {
	apiKey: string;
	children: ReactNode;
}) {
	const [client] = useState(() => new PostHog());
	const [ready, setReady] = useState(false);
	const initialized = useRef(false);
	const activeDecision = useRef<string | null>(null);
	const state = useAnalyticsConsentState();

	useEffect(() => {
		if (state === "loading") return;
		const sync = () => {
			if (readAnalyticsConsent() !== "granted") {
				if (initialized.current && activeDecision.current !== null) {
					client.opt_out_capturing();
					client.reset(true);
				}
				activeDecision.current = null;
				clearAnalyticsStorage(apiKey);
				setReady(false);
				return;
			}
			if (!initialized.current) {
				client.init(apiKey, {
					api_host: env.VITE_POSTHOG_HOST ?? "https://eu.i.posthog.com",
					opt_out_capturing_persistence_type: "localStorage",
					opt_out_capturing_by_default: true,
					opt_out_persistence_by_default: true,
					cross_subdomain_cookie: false,
					ip: false,
					cookie_expiration: 183,
					disable_session_recording: true,
					capture_pageview: false,
					capture_pageleave: false,
					autocapture: false,
					rageclick: false,
					capture_dead_clicks: false,
					enable_heatmaps: false,
					capture_heatmaps: false,
					capture_exceptions: false,
					capture_performance: false,
					person_profiles: "never",
					save_referrer: false,
					save_campaign_params: false,
					advanced_disable_flags: true,
					disable_external_dependency_loading: true,
					disable_surveys: true,
					disable_conversations: true,
					mask_all_element_attributes: true,
					mask_all_text: true,
					before_send: (event) =>
						readAnalyticsConsent() === "granted"
							? filterAnalyticsEvent(event)
							: null,
				});
				initialized.current = true;
			}
			const decidedAt = localStorage.getItem(ANALYTICS_CONSENT_AT_KEY);
			if (activeDecision.current !== decidedAt) {
				client.opt_in_capturing({
					captureEventName: false,
				});
				activeDecision.current = decidedAt;
			}
			setReady(true);
		};
		sync();
		// Withdraw immediately, including when another tab changes its choice.
		window.addEventListener(ANALYTICS_CONSENT_CHANGED_EVENT, sync);
		window.addEventListener("storage", sync);
		return () => {
			window.removeEventListener(ANALYTICS_CONSENT_CHANGED_EVENT, sync);
			window.removeEventListener("storage", sync);
		};
	}, [apiKey, client, state]);

	return (
		<PostHogProvider client={client}>
			<AnalyticsReadyContext.Provider value={ready}>
				{children}
			</AnalyticsReadyContext.Provider>
		</PostHogProvider>
	);
}
